import { WORLD_STATE } from "../data/worldState.js";
import { pushRemoteSnapshot } from "../scenes/features/networking/remotePlayers.js";

export function applyNetworkState(scene, msg) {
  if (!msg || !Array.isArray(msg.players)) return;
  const seen = new Set();
  const nowEpoch = Date.now();
  if (Number.isFinite(Number(msg?.tickRate))) {
    scene.netServerTickRate = Math.max(1, Math.floor(Number(msg.tickRate)));
  }
  let snapshotAt = nowEpoch;
  if (Number.isFinite(Number(msg?.serverTime))) {
    const serverTime = Number(msg.serverTime);
    const sampleOffset = nowEpoch - serverTime;
    if (!Number.isFinite(Number(scene.netServerTimeOffsetMs))) {
      scene.netServerTimeOffsetMs = sampleOffset;
    } else {
      scene.netServerTimeOffsetMs += (sampleOffset - scene.netServerTimeOffsetMs) * 0.1;
    }
    snapshotAt = serverTime + scene.netServerTimeOffsetMs;
  }

  for (const player of msg.players) {
    if (!player || !player.id) continue;
    if (player.connected === false) continue;
    if (player.id === scene.netPlayerId) {
      syncLocalPlayerFromServer(scene, player);
      continue;
    }
    seen.add(player.id);

    let remote = scene.remotePlayers.get(player.id);
    if (!remote) {
      remote = scene.createRemotePlayer(player);
      scene.remotePlayers.set(player.id, remote);
    }

    remote.targetX = Number.isFinite(player.x) ? player.x : remote.targetX;
    remote.targetY = Number.isFinite(player.y) ? player.y : remote.targetY;
    remote.vx = Number.isFinite(player.vx) ? player.vx : 0;
    remote.vy = Number.isFinite(player.vy) ? player.vy : 0;
    remote.stateReceivedAt = snapshotAt;
    remote.facing = player.facing >= 0 ? 1 : -1;
    remote.anim = typeof player.anim === "string" ? player.anim : "";
    if (typeof player.characterId === "string" && player.characterId.trim()) {
      const characterId = scene.sanitizeCharacterId(player.characterId);
      if (remote.characterId !== characterId) {
        remote.characterId = characterId;
        const idleAtlas = scene.resolvePlayableAtlasForState(characterId, "Idle");
        if (remote.sprite?.texture?.key !== idleAtlas) remote.sprite?.setTexture(idleAtlas);
        scene.applySpriteOriginForCharacter(remote.sprite, characterId);
        const idleAnim = scene.resolvePlayableAnimKey(characterId, "Idle");
        if (idleAnim && remote.sprite?.anims?.currentAnim?.key !== idleAnim) remote.sprite?.play(idleAnim);
      }
    }
    remote.name = player.name || remote.name || "Player";
    remote.stats = normalizePlayerStats(player.stats);
    if (remote.label) remote.label.setText(remote.name);
    remote.lastSeenAt = scene.time.now;
    pushRemoteSnapshot(remote, {
      receivedAt: snapshotAt,
      arrivedAt: nowEpoch,
      x: remote.targetX,
      y: remote.targetY,
      vx: remote.vx,
      vy: remote.vy,
      facing: remote.facing,
      anim: remote.anim,
    });
  }

  for (const playerId of scene.remotePlayers.keys()) {
    if (seen.has(playerId)) continue;
    scene.removeRemotePlayer(playerId);
  }
}

export function syncLocalPlayerFromServer(scene, player) {
  const p = scene.playerEntity;
  if (!p) return;
  scene.localPlayerStats = normalizePlayerStats(player.stats);
  if (typeof player?.name === "string") {
    scene.localPlayerNameText?.setText((player.name || "Player").slice(0, 24));
  }
  const serverHp = Number(player.hp);
  if (!Number.isFinite(serverHp)) return;
  if (typeof player.characterId === "string" && player.characterId.trim()) {
    const serverCharacterId = scene.sanitizeCharacterId(player.characterId);
    if (scene.localCharacterId !== serverCharacterId) {
      scene.localCharacterId = serverCharacterId;
      const idleAtlas = scene.resolvePlayableAtlasForState(serverCharacterId, "Idle");
      if (scene.playerSprite?.texture?.key !== idleAtlas) scene.playerSprite?.setTexture(idleAtlas);
      scene.applySpriteOriginForCharacter(scene.playerSprite, serverCharacterId);
      const idleAnim = scene.resolvePlayableAnimKey(serverCharacterId, "Idle");
      if (scene.playerSprite?.anims?.currentAnim?.key !== idleAnim) scene.playerSprite?.play(idleAnim);
    }
  }
  const lifeState = typeof player.lifeState === "string" ? player.lifeState : "";
  scene.netLifeState = lifeState || (serverHp > 0 ? "alive" : "dead");
  scene.netInvulnerableUntil = Number.isFinite(Number(player.invulnerableUntil)) ? Number(player.invulnerableUntil) : 0;

  const clampedHp = Math.max(0, Math.min(p.maxHp, serverHp));
  let resolvedHp = p.hp;
  if (clampedHp < p.hp) {
    const forceDownSync = lifeState === "dying" || lifeState === "dead" || clampedHp <= 0;
    if (forceDownSync) {
      p.takeDamage(p.hp - clampedHp, 0, "pvp");
      resolvedHp = clampedHp;
    }
  }
  p.hp = resolvedHp;
  WORLD_STATE.hp = p.hp;
  if ((lifeState === "dying" || lifeState === "dead" || p.hp <= 0) && !p.dying && !p.dead) {
    p.dying = true;
    p.dieTime = 0;
  }

}

function normalizePlayerStats(raw) {
  const safe = raw || {};
  const playerKills = Number.isFinite(Number(safe.playerKills)) ? Math.max(0, Math.floor(Number(safe.playerKills))) : 0;
  const playerDeaths = Number.isFinite(Number(safe.playerDeaths)) ? Math.max(0, Math.floor(Number(safe.playerDeaths))) : 0;
  const enemyKills = Number.isFinite(Number(safe.enemyKills)) ? Math.max(0, Math.floor(Number(safe.enemyKills))) : 0;
  const enemyDeaths = Number.isFinite(Number(safe.enemyDeaths)) ? Math.max(0, Math.floor(Number(safe.enemyDeaths))) : 0;
  return { playerKills, playerDeaths, enemyKills, enemyDeaths };
}
